import type { SharedColumnMeta } from './types'
import {
  COUNTRY_OPTIONS,
  EXCHANGE_OPTIONS,
  INDUSTRY_OPTIONS,
  SECTOR_OPTIONS,
  SHARE_GROUP_OPTIONS,
  YES_NO_INTEGER_OPTIONS,
} from './enum-options'

/**
 * Shared column metadata for columns that appear in 2+ feature catalogs.
 * This is the single source of truth for label, dataType, category, and
 * (for enum columns) the option list.
 *
 * Feature catalogs import from here to stay consistent, while adding
 * their own feature-specific extensions (e.g. scanner adds `isPremarket`,
 * key-stats adds `source`/`sourceField`, etc.).
 */
export const SHARED_COLUMNS: Record<string, SharedColumnMeta> = {
  // ─── Company ───
  // Enum-typed company columns (legacy multi-select parity): country, sector,
  // industry, exchange, share_group all carry their option lists here so
  // scanner and history don't need to wire them at every call-site.
  ticker: { label: 'Ticker', dataType: 'string', category: 'Company' },
  name: { label: 'Company Name', dataType: 'string', category: 'Company' },
  exchange: {
    label: 'Exchange',
    dataType: 'enum',
    category: 'Company',
    enumOptions: EXCHANGE_OPTIONS,
  },
  sector: {
    label: 'Sector',
    dataType: 'enum',
    category: 'Company',
    enumOptions: SECTOR_OPTIONS,
  },
  industry: {
    label: 'Industry',
    dataType: 'enum',
    category: 'Company',
    enumOptions: INDUSTRY_OPTIONS,
  },
  country: {
    label: 'Country',
    dataType: 'enum',
    category: 'Company',
    enumOptions: COUNTRY_OPTIONS,
  },
  ipo_date: { label: 'IPO Date', dataType: 'date', category: 'Company' },
  share_group: {
    label: 'Share Group',
    dataType: 'enum',
    category: 'Company',
    enumOptions: SHARE_GROUP_OPTIONS,
    allowNull: true,
  },

  // ─── Share Structure ───
  // Every column here carries `allowNull`: fundamentals are unreported for a
  // slice of the universe, so a "market cap < 1B" filter should be able to
  // keep names with no reported cap (Chris, 2026-09-13: share group, market
  // cap, shares out, float and their kin, never price or activity columns).
  // marketcap is a big-dollar magnitude (legacy `largePrice`), distinct from
  // share-volume flows like session_v.
  marketcap: {
    label: 'Market Cap',
    dataType: 'volume',
    category: 'Share Structure',
    columnType: 'largePrice',
    allowNull: true,
  },
  // Shares-outstanding / float are big discrete *share* counts — comparable
  // to each other but NOT to trade counts (which use `tradeCount`) or per-day
  // share volume flows (which use `volume`).
  sharesoutstanding: {
    label: 'Shares Out',
    dataType: 'volume',
    category: 'Share Structure',
    columnType: 'shareCount',
    allowNull: true,
  },
  sharesfloat: {
    label: 'Float',
    dataType: 'volume',
    category: 'Share Structure',
    columnType: 'shareCount',
    allowNull: true,
  },
  percentinsiders: {
    label: 'Insider Own %',
    dataType: 'percent',
    category: 'Share Structure',
    allowNull: true,
  },
  percentinstitutions: {
    label: 'Inst. Own %',
    dataType: 'percent',
    category: 'Share Structure',
    allowNull: true,
  },

  // ─── Pricing (OHLC) ───
  open: { label: 'Open', dataType: 'currency', category: 'Pricing' },
  high: { label: 'High', dataType: 'currency', category: 'Pricing' },
  low: { label: 'Low', dataType: 'currency', category: 'Pricing' },
  close: { label: 'Close', dataType: 'currency', category: 'Pricing' },

  // ─── Market Data ───
  volume: { label: 'Volume', dataType: 'volume', category: 'Market Data' },
  // RVOL is a relative-volume *ratio*, not a count or magnitude. Default to
  // `arbitraryNumber` so it doesn't accidentally compare to other numerics.
  rvol: {
    label: 'RVOL',
    dataType: 'number',
    category: 'Market Data',
    columnType: 'arbitraryNumber',
  },
  // SSR is an INTEGER 0/1 short-sale-restriction flag. Modelled as an enum
  // (multi-select Yes/No) — the user picks the friendly label and the filter
  // receives the integer-coded string '1' / '0'. Mirrors legacy.
  ssr: {
    label: 'SSR',
    dataType: 'enum',
    category: 'Market Data',
    enumOptions: YES_NO_INTEGER_OPTIONS,
  },
  eps: { label: 'EPS', dataType: 'currency', category: 'Valuation' },

  // ─── Pre-Market / After-Hours ───
  pm_lp: { label: 'PM Price', dataType: 'currency', category: 'Pre-Market / After-Hours' },
  // Backend column key is `day0_gap` (see ettalchemy column_mapping_definitions
  // line 38). Semantically a gap (yesterday's close → PM open), not a change.
  // Legacy production app + edge-ai skill docs both use "PM Gap" canonically.
  pm_chg: { label: 'PM Gap', dataType: 'currency', category: 'Pre-Market / After-Hours' },
  pm_chg_p: { label: 'PM Gap %', dataType: 'percent', category: 'Pre-Market / After-Hours' },
  ah_lp: { label: 'AH Price', dataType: 'currency', category: 'After-Hours' },
  ah_chg: { label: 'AH Change', dataType: 'currency', category: 'After-Hours' },
  ah_chg_p: { label: 'AH Change %', dataType: 'percent', category: 'After-Hours' },
}
